import React, { useState, useEffect } from 'react';
import { useAuth } from '@snipit/core/context/AuthContext';
import { Key, Phone, Loader2 } from 'lucide-react';
import { RecaptchaVerifier } from 'firebase/auth';
import { auth } from '@snipit/core/services/firebase';
import Footer from '../components/Footer';
import Logo from '@snipit/ui/components/Logo';

export default function LoginView() {
  const { loginWithGoogle, mfaResolver, sendMfaSignInCode, completeMfaSignIn, clearMfa } = useAuth();
  
  const [verificationId, setVerificationId] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Clean up recaptcha if we unmount or cancel
  useEffect(() => {
    return () => {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    };
  }, []);

  const handleSendCode = async () => {
    setError('');
    setLoading(true);
    try {
      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'mfa-send-button', {
          size: 'invisible'
        });
      }
      const vId = await sendMfaSignInCode(window.recaptchaVerifier);
      setVerificationId(vId);
    } catch (err) {
      console.error(err);
      setError('Failed to send SMS code. Please try again.');
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setError('');
    setLoading(true);
    try {
      await completeMfaSignIn(verificationId, smsCode);
    } catch (err) {
      console.error(err);
      setError('Invalid verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    clearMfa();
    setVerificationId('');
    setSmsCode('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="bg-[#121212] border border-gray-800 p-8 rounded-2xl shadow-2xl max-w-md w-full flex flex-col items-center text-center">
        {!mfaResolver ? (
          <>
            <Logo vault="dev" className="w-16 h-16 mb-4" />
            <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-linear-to-r from-blue-400 to-indigo-500 mb-2">
              SnipIT
            </h1>
            <p className="text-gray-400 mb-8">
              The ultimate SaaS for developers and AI engineers. Sign in to sync your vaults across Web, Desktop, and your Browser Extension.
            </p>
            
            <button 
              onClick={loginWithGoogle}
              className="w-full bg-white text-gray-900 font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-3 hover:bg-gray-200 transition-colors"
            >
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
              Sign in with Google
            </button>
            
            <div className="mt-8 text-xs text-gray-500 flex gap-4 justify-center">
              <a href="/privacy.html" target="_blank" className="hover:text-blue-400">Privacy Policy</a>
              <span>&bull;</span>
              <a href="/terms.html" target="_blank" className="hover:text-blue-400">Terms of Service</a>
            </div>
          </>
        ) : (
          <>
            <Key className="w-16 h-16 text-blue-500 mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">
              Two-Factor Authentication
            </h1>
            <p className="text-gray-400 mb-6 text-sm">
              Your account is protected by 2FA. Please verify your identity to continue.
            </p>
            
            {error && (
              <div className="w-full bg-red-900/30 border border-red-900/60 text-red-400 p-3 rounded mb-4 text-sm">
                {error}
              </div>
            )}

            {!verificationId ? (
              <div className="w-full space-y-4">
                <button
                  id="mfa-send-button"
                  onClick={handleSendCode}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Phone className="w-5 h-5" />}
                  Send SMS Code
                </button>
                <button 
                  onClick={handleCancel}
                  className="w-full text-gray-400 hover:text-white text-sm"
                >
                  Cancel Sign In
                </button>
              </div>
            ) : (
              <div className="w-full space-y-4">
                <input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value)}
                  className="w-full bg-[#1a1a1a] border border-gray-700 rounded p-3 text-center text-xl tracking-[0.5em] text-white focus:border-blue-500 focus:outline-none"
                  maxLength={6}
                />
                <button
                  onClick={handleVerify}
                  disabled={loading || smsCode.length < 6}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify'}
                </button>
                <button 
                  onClick={handleCancel}
                  className="w-full text-gray-400 hover:text-white text-sm"
                >
                  Back to Sign In
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <div className="absolute bottom-0 w-full">
        <Footer />
      </div>
    </div>
  );
}
